import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Textarea,
  keyframes,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import { FaArrowRight } from '@react-icons/all-files/fa/FaArrowRight';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaKeyboard } from '@react-icons/all-files/fa/FaKeyboard';
import { FaMicrophone } from '@react-icons/all-files/fa/FaMicrophone';
import { FaRedo } from '@react-icons/all-files/fa/FaRedo';
import { memo, useState } from 'react';

import colors from '../../chakra/foundations/colors';
import Title from '../../components/Title';
import Button from '../../components/ui/buttons/Button';
import useVoiceRecorder, {
  CAP_WARNING_SECONDS,
  RECORDING_CAP_SECONDS,
} from '../../hooks/useVoiceRecorder';
import { formatSecondsAsTimer } from '../../utils/helpers';
import RatingPill from './RatingPill';
import TakePlayback from './TakePlayback';
import { isLikelyInAppBrowser } from './surveyEnvironment';

const LIVE_BAR_COUNT = 28;

const waveBounce = keyframes`
  from { transform: scaleY(0.17); }
  to { transform: scaleY(1); }
`;

const ringPulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  70% { transform: scale(1.28); opacity: 0; }
  100% { transform: scale(1.28); opacity: 0; }
`;

const listeningBlink = keyframes`
  to { opacity: 0.25; }
`;

export type VoiceAnswer =
  | { kind: 'voice'; blob: Blob; mimeType: string; durationSeconds: number; rating?: number }
  | { kind: 'text'; text: string; rating?: number };

type VoiceQuestionProps = {
  question: string;
  subtitle?: string;
  hasRating?: boolean;
  isLastQuestion?: boolean;
  isSaving?: boolean;
  sendFailed?: boolean;
  onAccept: (answer: VoiceAnswer) => void;
};

const RatingGrid = ({
  value,
  onPick,
}: {
  value: number | null;
  onPick: (rating: number) => void;
}) => (
  <Box role="group" aria-label="Rate from 1, not easy, to 10, super easy">
    <SimpleGrid columns={5} spacing={2}>
      {Array.from({ length: 10 }, (_, index) => {
        const gridValue = index + 1;
        const isSelected = value === gridValue;
        return (
          <Button
            key={gridValue}
            variant="unstyled"
            aria-pressed={isSelected}
            onClick={() => onPick(gridValue)}
            h="46px"
            borderRadius="8px"
            fontWeight={700}
            fontSize="16px"
            border={isSelected ? 'none' : '1.5px solid'}
            borderColor="gainsboro"
            bg={isSelected ? 'leaseEndBlue' : 'white'}
            color={isSelected ? 'leaseEndYellow' : 'leaseEndGray'}
          >
            {gridValue}
          </Button>
        );
      })}
    </SimpleGrid>
    <Flex justify="space-between" mt={2} fontSize="11.5px" color="silver" fontWeight={600}>
      <Text>Not easy</Text>
      <Text>Super easy</Text>
    </Flex>
  </Box>
);

const QuestionHeader = ({ question, subtitle }: { question: string; subtitle?: string }) => (
  <>
    <Title as="h2" fontSize="25px" lineHeight={1.25} textAlign="left" letterSpacing="-0.3px">
      {question}
    </Title>
    {subtitle && (
      <Text mt={2.5} fontSize="15px" color="grayLight" fontWeight={500} lineHeight={1.5}>
        {subtitle}
      </Text>
    )}
  </>
);

const RecordButton = ({ onTap, isDisabled }: { onTap: () => void; isDisabled: boolean }) => (
  <Button
    variant="unstyled"
    aria-label="Record answer"
    onClick={onTap}
    isDisabled={isDisabled}
    position="relative"
    w="132px"
    h="132px"
    borderRadius="full"
    bg="leaseEndYellow"
    boxShadow={`0 10px 24px ${colors.leaseEndBlueGlow}`}
    display="flex"
    alignItems="center"
    justifyContent="center"
    _active={{ transform: 'scale(0.96)' }}
  >
    <Box
      position="absolute"
      inset="-10px"
      borderRadius="full"
      border="2px solid"
      borderColor="leaseEndYellowShadow"
    />
    <Box color="leaseEndBlue" fontSize="46px">
      <FaMicrophone />
    </Box>
  </Button>
);

const StopButton = ({ onTap, reducedMotion }: { onTap: () => void; reducedMotion: boolean }) => (
  <Button
    variant="unstyled"
    aria-label="Stop recording"
    onClick={onTap}
    position="relative"
    w="132px"
    h="132px"
    borderRadius="full"
    bg="leaseEndBlue"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Box
      position="absolute"
      inset={0}
      borderRadius="full"
      border="3px solid"
      borderColor="leaseEndRed"
      animation={reducedMotion ? undefined : `${ringPulse} 1.4s ease-out infinite`}
    />
    <Box w="40px" h="40px" borderRadius="9px" bg="white" />
  </Button>
);

const LiveWaveform = memo(
  ({ nearCap, reducedMotion }: { nearCap: boolean; reducedMotion: boolean }) => (
    <Flex align="center" justify="center" gap={1} h="56px">
      {Array.from({ length: LIVE_BAR_COUNT }, (_, index) => (
        <Box
          key={index}
          w="4px"
          borderRadius="full"
          bg={nearCap ? 'darkGoldenrod' : 'leaseEndSkyBlue'}
          h={reducedMotion ? `${12 + (index % 7) * 5}px` : '46px'}
          animation={
            reducedMotion
              ? undefined
              : `${waveBounce} 0.9s ${(index % 7) * 0.08}s ease-in-out infinite alternate`
          }
        />
      ))}
    </Flex>
  ),
);

const SendFailedNotice = () => (
  <Text role="alert" textAlign="center" fontSize="13px" fontWeight={600} color="darkGoldenrod">
    That didn't send, tap to try again.
  </Text>
);

const VoiceQuestion = ({
  question,
  subtitle,
  hasRating,
  isLastQuestion,
  isSaving,
  sendFailed,
  onAccept,
}: VoiceQuestionProps) => {
  const { status, isSupported, micError, elapsedSeconds, tooShort, take, start, stop, redo } =
    useVoiceRecorder();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [rating, setRating] = useState<number | null>(null);
  const [userChoseText, setUserChoseText] = useState(false);
  const [text, setText] = useState('');

  const needsRating = hasRating && rating == null;
  const micUnavailable = !isSupported || !!micError;
  const forcedTextMode = micUnavailable || isLikelyInAppBrowser();
  const showTextFallback = forcedTextMode || userChoseText;

  const acceptVoice = () => {
    if (!take) return;
    onAccept({
      kind: 'voice',
      blob: take.blob,
      mimeType: take.mimeType,
      durationSeconds: take.durationSeconds,
      rating: rating ?? undefined,
    });
  };

  const acceptText = () =>
    onAccept({ kind: 'text', text: text.trim(), rating: rating ?? undefined });

  if (showTextFallback) {
    return (
      <Flex direction="column" flex={1} w="full">
        <Flex direction="column" flex={1} px={7} pt={5}>
          {micError === 'denied' && (
            <Flex
              gap={2.5}
              align="flex-start"
              bg="amberLight"
              border="1px solid"
              borderColor="bananaMania"
              borderRadius="12px"
              px={3.5}
              py={3}
              mb={4}
            >
              <Box color="darkGoldenrod" mt={0.5}>
                <FaKeyboard />
              </Box>
              <Text fontSize="13.5px" color="leaseEndGray" fontWeight={500} lineHeight={1.45}>
                Looks like your mic's blocked. No problem, you can type this one instead.
              </Text>
            </Flex>
          )}
          <QuestionHeader question={question} subtitle={subtitle} />
          {hasRating && (
            <Box mt={4} mb={1.5}>
              <RatingGrid value={rating} onPick={setRating} />
            </Box>
          )}
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            aria-label="Type your answer"
            placeholder="Type your answer here…"
            mt={4}
            minH="130px"
            resize="none"
            fontSize="16px"
          />
        </Flex>
        <Flex direction="column" align="center" gap={2.5} px={5} pb={6} pt={3.5}>
          {sendFailed && <SendFailedNotice />}
          <Button
            w="full"
            textTransform="uppercase"
            rightIcon={<FaArrowRight />}
            onClick={acceptText}
            isLoading={isSaving}
            isDisabled={!text.trim() || needsRating}
          >
            {isLastQuestion ? 'Finish' : 'Next question'}
          </Button>
          {!forcedTextMode && (
            <Button
              variant="link"
              leftIcon={<FaMicrophone />}
              onClick={() => setUserChoseText(false)}
              fontSize="13.5px"
              fontWeight={600}
            >
              Changed your mind? Record it instead
            </Button>
          )}
        </Flex>
      </Flex>
    );
  }

  if (status === 'recording') {
    const nearCap = elapsedSeconds >= CAP_WARNING_SECONDS;
    return (
      <Flex direction="column" flex={1} w="full">
        <Box flex={1} px={7} pt={6}>
          <Title
            as="h2"
            fontSize="21px"
            lineHeight={1.3}
            textAlign="left"
            letterSpacing="-0.2px"
            opacity={0.85}
          >
            {question}
          </Title>
        </Box>
        <Flex direction="column" align="center" px={7} pb={7}>
          <Flex align="center" gap={2} mb={2.5}>
            <Box
              w="9px"
              h="9px"
              borderRadius="full"
              bg="leaseEndRed"
              animation={
                prefersReducedMotion ? undefined : `${listeningBlink} 1s steps(2, start) infinite`
              }
            />
            <Text
              fontSize="13.5px"
              fontWeight={700}
              letterSpacing="1px"
              textTransform="uppercase"
              color="leaseEndRed"
            >
              Listening
            </Text>
          </Flex>
          <Text
            fontFamily="heading"
            fontWeight={600}
            fontSize="48px"
            color="leaseEndBlue"
            letterSpacing="-1px"
            lineHeight={1}
          >
            {formatSecondsAsTimer(elapsedSeconds)}
          </Text>
          <Box h="24px" mt={1.5}>
            {nearCap && (
              <Flex align="center" gap={1.5} fontSize="13px" fontWeight={600} color="darkGoldenrod">
                <FaInfoCircle />
                <Text>Wrapping up in {RECORDING_CAP_SECONDS - elapsedSeconds}s…</Text>
              </Flex>
            )}
          </Box>
          <Box w="full" mt={3} mb={6}>
            <LiveWaveform nearCap={nearCap} reducedMotion={prefersReducedMotion} />
          </Box>
          <StopButton onTap={stop} reducedMotion={prefersReducedMotion} />
          <Text mt={4} fontSize="14px" fontWeight={600} color="leaseEndGray">
            Tap to stop
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (take) {
    return (
      <Flex direction="column" flex={1} w="full">
        <Flex direction="column" flex={1} justify="center" px={7}>
          <Flex align="center" gap={2} color="greenDark" fontWeight={700} fontSize="13px" mb={3}>
            <FaCheckCircle />
            <Text>Got it</Text>
          </Flex>
          <Title
            as="h2"
            fontSize="24px"
            lineHeight={1.2}
            textAlign="left"
            letterSpacing="-0.3px"
            mb={4}
          >
            Here's your take, sound good?
          </Title>
          {hasRating && rating != null && (
            <RatingPill rating={rating} alignSelf="flex-start" mb={3.5} />
          )}
          <TakePlayback take={take} />
        </Flex>
        <Flex direction="column" gap={2.5} px={5} pb={6}>
          {sendFailed && <SendFailedNotice />}
          <Button
            w="full"
            textTransform="uppercase"
            rightIcon={<FaArrowRight />}
            onClick={acceptVoice}
            isLoading={isSaving}
          >
            {isLastQuestion ? 'Sounds good, finish' : 'Sounds good, next'}
          </Button>
          <Button
            w="full"
            variant="secondary"
            leftIcon={<FaRedo />}
            onClick={redo}
            isDisabled={isSaving}
          >
            Redo this one
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" flex={1} w="full">
      <Flex direction="column" flex={1} px={7} pt={6}>
        <QuestionHeader question={question} subtitle={subtitle} />
        {hasRating && (
          <Box mt={5}>
            <RatingGrid value={rating} onPick={setRating} />
          </Box>
        )}
      </Flex>
      <Flex direction="column" align="center" px={7} pb={6} pt={2.5}>
        <Flex direction="column" align="center" opacity={needsRating ? 0.4 : 1} textAlign="center">
          <RecordButton onTap={start} isDisabled={needsRating || status === 'starting'} />
          <Text mt={4} fontSize="15px" fontWeight={600} color="leaseEndGray">
            {hasRating ? 'Tap to record your why' : 'Tap to record your answer'}
          </Text>
        </Flex>
        <Text
          mt={2.5}
          fontSize="13px"
          color={tooShort ? 'darkGoldenrod' : 'silver'}
          minH="18px"
          fontWeight={500}
        >
          {needsRating
            ? 'Pick a number first ☝️'
            : tooShort
            ? "Didn't catch that, tap to record"
            : 'Speak naturally, you can redo it after.'}
        </Text>
        <Button
          variant="link"
          onClick={() => setUserChoseText(true)}
          isDisabled={status === 'starting'}
          mt={2}
          fontSize="13.5px"
          fontWeight={600}
        >
          Can't record? Type it instead
        </Button>
      </Flex>
    </Flex>
  );
};

export default VoiceQuestion;
